import common from "../../common";
import Item from "./Item";
import {translate} from "../../utils";

export default class BaseCarousel extends PIXI.Container {

    static wrap(x, m) {
        return (x % m + m) % m;
    }

    get selected() {
        return this._items[this._index];
    }

    get index() {
        return this._index;
    }

    constructor(startIndex = 0) {
        super();

        this.signals = {};
        this.signals.selected = new P3.utils.MiniSignal();

        this._tweens = new P3.utils.TWEEN.Group();

        this._index = startIndex;
        this._items = [];

        this._itemHolder = new PIXI.Container();
        this.addChild(this._itemHolder);

        this._itemOffset = 180;
        this._buttonOffset = 440;

        this._interaction = new PIXI.Container();
        this._interaction.hitArea = new PIXI.Rectangle(-common.STAGE_WIDTH * 0.5, -common.STAGE_HEIGHT * 0.5, common.STAGE_WIDTH, common.STAGE_HEIGHT);
        this._interaction.interactive = true;
        this._interaction.touchstart = this._onTouchStart.bind(this);
        this._interaction.touchend = this._interaction.touchendoutside = this._onTouchEnd.bind(this);
        this._interaction.touchmove = this._onTouchMove.bind(this);
        this.addChild(this._interaction);

        let states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_left");

        this._prevButton = new P3.Button(states);
        this._prevButton.position.set(-this._buttonOffset, 0);
        this._prevButton.signals.down.add(this._onPrevDown, this);
        this._prevButton.signals.up.add(this._onPrevUp, this);
        //  this._prevButton.downSoundName = "sfx_btn_ui_next_drawing_00";
        //this._prevButton.visible = false;
        this.addChild(this._prevButton);

        states.normal = P3.assets.texture("btn_right");
        this._nextButton = new P3.Button(states);
        this._nextButton.position.set(this._buttonOffset, 0);
        this._nextButton.signals.down.add(this._onNextDown, this);
        this._nextButton.signals.up.add(this._onNextUp, this);
        //  this._nextButton.downSoundName = "sfx_btn_ui_prev_drawing_00";
        //this._nextButton.visible = false;
        this.addChild(this._nextButton);

        states = new P3.ButtonStates();

        states.normal = P3.assets.texture("btn_secondary_out");
        states.down = P3.assets.texture("btn_secondary_down");
        states.over = P3.assets.texture("btn_secondary_over");
        states.icon = P3.assets.texture("icon_play_small");

        this._selectButton = new P3.Button(states);
        this._selectButton.position.set(0, 195);
        this._selectButton.signals.click.add(this._onSelect, this);
        this._selectButton.clickSoundName = "sfx_click";
        //this._selectButton.visible = false;
        this.addChild(this._selectButton);

        this.hide();
    }

    destroy(options) {
        this._tweens.removeAll(); // make sure to kill these tweens before destroy
        super.destroy(options);
    }

    init(data) {
        for (let i = 0; i < data.length; i++) {
            const item = new Item(data[i]);
            this._itemHolder.addChild(item);
            this._items.push(item);
        }

        this._setItemPositions();
    }

    animateIn(delay = 0) {
        this._interaction.interactive = true;

        const uiElements = [this._prevButton, this._nextButton, this._selectButton];

        for (let i = 0; i < uiElements.length; i++) {
            const e = uiElements[i];
            e.scale.set(0.3);

            new P3.utils.TWEEN.Tween(e.scale, this._tweens)
                .delay(i * 200 + delay)
                .to({x: 1, y: 1}, 520)
                .easing(P3.utils.TWEEN.Easing.Back.Out)
                .onStart(() => e.visible = true)
                .start();
        }
    }

    reveal() {
        this._nextButton.visible = true;
        this._prevButton.visible = true;
        this._selectButton.visible = true;
        this._interaction.interactive = true;
    }

    hide() {
        this._nextButton.visible = false;
        this._prevButton.visible = false;
        this._selectButton.visible = false;
        this._interaction.interactive = false;
    }

    _setItemPositions() {
        // wrap side indices
        const leftIndex = (this._index - 1 < 0) ? this._items.length - 1 : this._index - 1;
        const rightIndex = (this._index + 1 > this._items.length - 1) ? 0 : this._index + 1;

        for (let i = 0; i < this._items.length; i++) {
            const item = this._items[i];
            item.visible = true;

            if (i === this._index) {

                this._itemHolder.addChild(item);
                this.lockSelectButton(item.unlocked);

                if (this._items.length > 2) {
                    item.x = 0;
                    item.scale.set(1);
                }
            } else if (i === leftIndex) {
                if (this._items.length > 2) {
                    item.x = -this._itemOffset;
                    item.scale.set(0.9);
                    item.rotation = -Math.PI * 0.02;
                }
            } else if (i === rightIndex) {
                if (this._items.length > 2) {
                    item.x = this._itemOffset;
                    item.scale.set(0.9);
                    item.rotation = Math.PI * 0.02;
                }
            } else {
                item.visible = false;
            }
        }
    }

    _updateItemPositions(lastLeftIndex, lastRightIndex) {
        this._animating = true;

        // wrap side indices
        const leftIndex = (this._index - 1 < 0) ? this._items.length - 1 : this._index - 1;
        const rightIndex = (this._index + 1 > this._items.length - 1) ? 0 : this._index + 1;

        const t = 220;

        for (let i = 0; i < this._items.length; i++) {
            const item = this._items[i];

            if (i === this._index) {
                item.visible = true;
                //item.label.visible = true;

                if (this._items.length > 2) {
                    P3.utils.delayg(this._tweens, t / 1000 * 0.25, () => this._itemHolder.addChild(item));
                    this.lockSelectButton(item.unlocked);

                    new P3.utils.TWEEN.Tween(item, this._tweens)
                        .to({x: 0, alpha: 1, rotation: 0}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .onComplete(() => {
                            P3.utils.delayg(this._tweens, 0.1, () => this._animating = false);
                        })
                        .start();

                    new P3.utils.TWEEN.Tween(item.scale, this._tweens)
                        .to({x: 1, y: 1}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();

                } else {
                    this._itemHolder.addChild(item)
                }


            } else if (i === leftIndex) {

                if (!item.visible && this._items.length > 2) {
                    item.alpha = 0;
                    item.x = -this._itemOffset * 1.5;
                    item.scale.set(0.8);
                }

                item.visible = true;
                // item.label.visible = true;

                if (this._items.length > 2) {
                    new P3.utils.TWEEN.Tween(item, this._tweens)
                        .to({x: -this._itemOffset, alpha: 1, rotation: -Math.PI * 0.02}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();

                    new P3.utils.TWEEN.Tween(item.scale, this._tweens)
                        .to({x: 0.9, y: 0.9}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();
                }

            } else if (i === rightIndex) {

                if (!item.visible && this._items.length > 2) {
                    item.alpha = 0;
                    item.x = this._itemOffset * 1.5;
                    item.scale.set(0.8);
                }

                item.visible = true;
                //  item.label.visible = true;
                if (this._items.length > 2) {
                    new P3.utils.TWEEN.Tween(item, this._tweens)
                        .to({x: this._itemOffset, alpha: 1, rotation: Math.PI * 0.02}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();

                    new P3.utils.TWEEN.Tween(item.scale, this._tweens)
                        .to({x: 0.9, y: 0.9}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();
                }

            } else if (i === lastLeftIndex) {
                // tween it out to the side and fade out
                item.visible = true;
                //  item.label.visible = true;
                if (this._items.length > 2) {
                    new P3.utils.TWEEN.Tween(item, this._tweens)
                        .to({x: -this._itemOffset * 1.5, alpha: 0}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();

                    new P3.utils.TWEEN.Tween(item.scale, this._tweens)
                        .to({x: 0.8, y: 0.8}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();
                }

            } else if (i === lastRightIndex) {
                // tween it out to the side and fade out
                item.visible = true;
                //  item.label.visible = true;
                if (this._items.length > 2) {
                    new P3.utils.TWEEN.Tween(item, this._tweens)
                        .to({x: this._itemOffset * 1.5, alpha: 0}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();

                    new P3.utils.TWEEN.Tween(item.scale, this._tweens)
                        .to({x: 0.8, y: 0.8}, t)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .start();
                }
            } else {
                item.visible = false;
            }
        }

        if (this._items.length <= 2) {
            P3.utils.delayg(this._tweens, 0.25, () => this._animating = false);
        }
    }

    lockSelectButton(unlocked) {

    }

    update(dt) {
        this._tweens.update();
        if (this._scrollDir < 0 || P3.input.isKeyDown(P3.keys.LEFT)) {
            this.prev();
        }
        if (this._scrollDir > 0 || P3.input.isKeyDown(P3.keys.RIGHT)) {
            this.next();
        }
    }

    next() {
        if (this._animating || this._selectionMade)
            return;

        const leftIndex = (this._index - 1 < 0) ? this._items.length - 1 : this._index - 1;
        const rightIndex = (this._index + 1 > this._items.length - 1) ? 0 : this._index + 1;

        this._index++;
        this._index = BaseCarousel.wrap(this._index, this._items.length);
        this._updateItemPositions(leftIndex, rightIndex);

        P3.audio.playSound("sfx_btn_ui_next_drawing_00");
    }

    prev() {
        if (this._animating || this._selectionMade)
            return;

        const leftIndex = (this._index - 1 < 0) ? this._items.length - 1 : this._index - 1;
        const rightIndex = (this._index + 1 > this._items.length - 1) ? 0 : this._index + 1;

        this._index--;
        this._index = BaseCarousel.wrap(this._index, this._items.length);
        this._updateItemPositions(leftIndex, rightIndex);

        P3.audio.playSound("sfx_btn_ui_prev_drawing_00");
    }

    _onPrevDown() {
        this._scrollDir = -1;
    }

    _onNextDown() {
        this._scrollDir = 1;
    }

    _onPrevUp() {
        this._scrollDir = 0;
    }

    _onNextUp() {
        this._scrollDir = 0;
    }

    _onTouchStart(event) {
        this._down = true;
        this._touchStart = event.data.global.clone();
    }

    _onTouchEnd(event) {
        this._down = false;
    }

    _onTouchMove(event) {
        if (this._down) {
            const global = event.data.global.clone();
            const swipeDistance = 80;
            if (this._touchStart.x - global.x > swipeDistance) {
                this.next();
                this._touchStart = event.data.global.clone();
            } else if (this._touchStart.x - global.x < -swipeDistance) {
                this.prev();
                this._touchStart = event.data.global.clone();
            }
        }
    }

    _onSelect() {
        this.signals.selected.dispatch();
    }
}